/**
 * Taxonomy Routes (Hono + OpenAPI)
 *
 * Generic REST endpoints for tag assignments across entities.
 */

import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import type { MiddlewareHandler } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'
import { requireAbility } from '../../http/middleware/ability.middleware.js'
import type { Subjects } from '@kernel/abilities/ability.types.js'
import { createTaxonomyController } from './taxonomy.controller.js'

export function createTaxonomyRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()
  const controller = createTaxonomyController(ctx)

  const authMw = ctx.core.middleware['auth']
  if (!authMw) {
    throw new Error('Auth middleware is required for taxonomy routes')
  }
  const auth = authMw as unknown as MiddlewareHandler<NexusEnv>

  const canCreateAssignment = requireAbility(
    'create',
    'TaxonomyTagAssignment' as Subjects
  ) as unknown as MiddlewareHandler<NexusEnv>
  const canDeleteAssignment = requireAbility(
    'delete',
    'TaxonomyTagAssignment' as Subjects
  ) as unknown as MiddlewareHandler<NexusEnv>

  const anyObject = z.object({}).passthrough()
  const anyJson = z.unknown()

  const entityParams = z.object({
    entityType: z.string(),
    entityId: z.string()
  })
  const tagParams = z.object({ tagId: z.string() })
  const entityTagParams = entityParams.extend({ tagId: z.string() })

  // GET /entities/:entityType/:entityId/tags
  const listEntityTagsRoute = createRoute({
    method: 'get',
    path: '/entities/{entityType}/{entityId}/tags',
    tags: ['Taxonomy'],
    summary: 'List tags for an entity',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: { params: entityParams },
    responses: {
      200: { description: 'Tag list', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(listEntityTagsRoute, controller.listEntityTags as OpenApiHandler)

  // PUT /entities/:entityType/:entityId/tags
  const syncEntityTagsRoute = createRoute({
    method: 'put',
    path: '/entities/{entityType}/{entityId}/tags',
    tags: ['Taxonomy'],
    summary: 'Sync (replace) all tags for an entity',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canCreateAssignment] as const,
    request: {
      params: entityParams,
      body: {
        content: {
          'application/json': {
            schema: z.object({ tagIds: z.array(z.string()) })
          }
        }
      }
    },
    responses: {
      200: { description: 'Updated tag list', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } },
      403: { description: 'Forbidden', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(syncEntityTagsRoute, controller.syncEntityTags as OpenApiHandler)

  // POST /entities/:entityType/:entityId/tags
  const assignEntityTagRoute = createRoute({
    method: 'post',
    path: '/entities/{entityType}/{entityId}/tags',
    tags: ['Taxonomy'],
    summary: 'Assign a single tag to an entity',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canCreateAssignment] as const,
    request: {
      params: entityParams,
      body: {
        content: {
          'application/json': {
            schema: z.object({ tagId: z.string() })
          }
        }
      }
    },
    responses: {
      201: { description: 'Assignment created', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } },
      403: { description: 'Forbidden', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(assignEntityTagRoute, controller.assignEntityTag as OpenApiHandler)

  // DELETE /entities/:entityType/:entityId/tags/:tagId
  const unassignEntityTagRoute = createRoute({
    method: 'delete',
    path: '/entities/{entityType}/{entityId}/tags/{tagId}',
    tags: ['Taxonomy'],
    summary: 'Unassign a tag from an entity',
    security: [{ bearerAuth: [] }],
    middleware: [auth, canDeleteAssignment] as const,
    request: { params: entityTagParams },
    responses: {
      204: { description: 'Assignment removed' },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } },
      403: { description: 'Forbidden', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(unassignEntityTagRoute, controller.unassignEntityTag as OpenApiHandler)

  // GET /stats
  const statsRoute = createRoute({
    method: 'get',
    path: '/stats',
    tags: ['Taxonomy'],
    summary: 'Aggregated taxonomy stats (dashboard widget)',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Stats', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(statsRoute, controller.getStats as OpenApiHandler)

  // GET /stats/by-type
  const statsByTypeRoute = createRoute({
    method: 'get',
    path: '/stats/by-type',
    tags: ['Taxonomy'],
    summary: 'Assignment count by entity type',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Chart data', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(statsByTypeRoute, controller.getStatsByType as OpenApiHandler)

  // GET /stats/top-tags
  const topTagsRoute = createRoute({
    method: 'get',
    path: '/stats/top-tags',
    tags: ['Taxonomy'],
    summary: 'Top 10 most assigned tags',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Top tags list', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(topTagsRoute, controller.getStatsTopTags as OpenApiHandler)

  // GET /tags/:tagId/entities
  const entitiesForTagRoute = createRoute({
    method: 'get',
    path: '/tags/{tagId}/entities',
    tags: ['Taxonomy'],
    summary: 'List entities assigned to a tag (optional entityType filter)',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: {
      params: tagParams,
      query: z.object({ entityType: z.string().optional() })
    },
    responses: {
      200: { description: 'Entity refs', content: { 'application/json': { schema: anyJson } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(entitiesForTagRoute, controller.listEntitiesForTag as OpenApiHandler)

  return app
}
